import { Component, Inject, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'gtw-import-upload-dialog',
    template: `
    <div class="modal-header">
      <h3 class="modal-title">{{ modalTitle }}</h3>
      <div class="header-actions">
        <i class="fa"
           [ngClass]="{'fa-plus-square-o': minimised, 'fa-minus-square-o': !minimised}"
           style="margin-right: 10px; cursor: pointer;"
           (click)="minimised = !minimised"></i>
        <i (click)="close()" class="fa fa-times" style="cursor: pointer;"></i>
      </div>
    </div>

    @if (!minimised) {
<div class="modal-body">
      <div class="drop-box"
           [class.drag-over]="isDragOver"
           (dragenter)="onDragEnter($event)"
           (dragover)="onDragOver($event)"
           (dragleave)="onDragLeave($event)"
           (drop)="onDrop($event)"
           (click)="fileInput.click()">
        <span>
          Select template by clicking here Or Drag and drop your template here
          <p>
            @if (!onlyTxtFiles) {
<span>
              Only files with extension "*.xls, xlt, xlm, xlsx, xlsm, xltx, xltm" are allowed.
            </span>
}
            @if (onlyTxtFiles) {
<span>
              Only files with extension ".txt" is allowed.
            </span>
}
          </p>
        </span>
        <input #fileInput type="file" style="display:none"
               [accept]="acceptTypes"
               (change)="onFileSelected($event)">
      </div>

      @if (selectedFile) {
<div class="file-status">
        @if (uploading) {
<div class="upload-progress">
          <i class="fa fa-spinner fa-spin"></i> Uploading {{ selectedFile.name }}...
          <div class="progress">
            <div class="progress-bar progress-bar-info progress-bar-striped active"
                 [style.width.%]="uploadProgress"></div>
          </div>
        </div>
}
        @if (uploadComplete && !fileError) {
<div class="upload-success">
          Upload complete for {{ selectedFile.name }}
        </div>
}
        @if (fileError) {
<div class="upload-error">
          Upload failed: {{ errorMessage }}
        </div>
}
      </div>
}
    </div>
}

    @if (!minimised) {
<div class="modal-footer">
      <button class="cancel-btn" (click)="close()">Cancel</button>
    </div>
}
  `,
    styles: [`
    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      border-bottom: 1px solid #a9440f;
      background: #c65a1e;
      color: #fff;
    }
    .modal-title {
      margin: 0;
      font-size: 18px;
      font-weight: bold;
      color: #fff;
    }
    .header-actions {
      display: flex;
      align-items: center;
      margin-left: auto;
      gap: 8px;
    }
    .header-actions i {
      font-size: 16px;
      color: #fff;
    }
    .header-actions i:hover {
      color: #f7e7d8;
    }
    .modal-body {
      padding: 8px 10px;
      background: #fff;
    }
    .modal-footer {
      padding: 6px 10px 8px;
      text-align: right;
      border-top: 1px solid #e5e5e5;
      background: #fff;
    }
    .modal-footer .cancel-btn {
      background: #f0ad4e;
      border: 1px solid #eea236;
      color: #fff;
      font-size: 11px;
      padding: 1px 6px;
      line-height: 1.5;
      border-radius: 2px;
    }
    .modal-footer .cancel-btn:hover {
      background: #ec971f;
      border-color: #d58512;
    }
    .drop-box {
      background: #f4f1df;
      padding: 10px 12px;
      text-align: left;
      cursor: pointer;
      margin: 0;
      transition: border-color 0.2s;
      min-height: 44px;
      font-size: 12px;
      color: #c09853;
    }
    .drop-box p {
      margin: 0;
      color: #c09853;
      font-size: 12px;
    }
    .drop-box.drag-over {
      border-color: #37d631;
    }
    .file-status {
      margin: 8px 0 0;
    }
    .upload-progress { color: #333; }
    .upload-success { color: green; }
    .upload-error { color: red; }
    .progress { margin-top: 5px; }
  `],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ImportUploadDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  modalTitle = 'Existing Batch Transactions';
  minimised = false;
  isDragOver = false;
  selectedFile: File | null = null;
  uploading = false;
  uploadComplete = false;
  uploadProgress = 0;
  fileError = false;
  errorMessage = '';
  onlyTxtFiles = false;
  acceptTypes = '.xls,.xlt,.xlm,.xlsx,.xlsm,.xltx,.xltm,.txt';

  private readonly ALLOWED_EXTENSIONS = ['xls', 'xlt', 'xlm', 'xlsx', 'xlsm', 'xltx', 'xltm', 'txt'];

  constructor(
    private dialogRef: MatDialogRef<ImportUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private http: HttpClient,
    private gtwSnackbarService: GtwSnackbarService
  ) {
    const txtOnlyScreens = ['366', '237'];
    if (data?.title) {
      this.modalTitle = data.title;
    }
    if (txtOnlyScreens.includes(data?.screenKey)) {
      this.onlyTxtFiles = true;
      this.acceptTypes = '.txt';
    }
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.processFile(files[0]);
    }
  }

  onFileSelected(event: any): void {
    const file = event.target?.files?.[0];
    if (file) {
      this.processFile(file);
    }
  }

  private processFile(file: File): void {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!this.ALLOWED_EXTENSIONS.includes(ext)) {
      this.fileError = true;
      this.errorMessage = `${file.name} is invalid. Only ${this.onlyTxtFiles ? '.txt' : 'xls/xlsx/txt'} files are allowed.`;
      this.selectedFile = file;
      return;
    }

    this.selectedFile = file;
    this.fileError = false;
    this.errorMessage = '';
    this.uploadFile(file);
  }

  private uploadFile(file: File): void {
    this.uploading = true;
    this.uploadComplete = false;
    this.uploadProgress = 0;

    const chunkSize = 104857600; // 100MB — flow.js default
    const flowIdentifier =
      file.size + '-' + file.name.replace(/[^a-zA-Z0-9]/g, '');

    const formData = new FormData();
    formData.append('flowChunkNumber', '1');
    formData.append('flowChunkSize', String(chunkSize));
    formData.append('flowCurrentChunkSize', String(file.size));
    formData.append('flowTotalSize', String(file.size));
    formData.append('flowIdentifier', flowIdentifier);
    formData.append('flowFilename', file.name);
    formData.append('flowRelativePath', file.name);
    formData.append('flowTotalChunks', '1');
    formData.append('file', file, file.name);

    const uploadUrl = `${environment.base_url}/uploadTemplate`;

    this.http
      .post<any>(uploadUrl, formData, {
        reportUploadProgress: true,
        observe: 'events',
        withCredentials: true,
      }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadProgress = event.total
              ? Math.round((100 * event.loaded) / event.total)
              : 0;
          } else if (event.type === HttpEventType.Response) {
            this.uploading = false;
            const body = event.body;

            if (body?.uploadStatus === 'success') {
              this.uploadComplete = true;
              this.loadGridDataFromTemplate(body.flowFilename);
            } else if (body?.uploadStatus === 'error') {
              this.fileError = true;
              this.errorMessage = body.message || 'Upload failed.';
            } else {
              this.uploadComplete = true;
              this.loadGridDataFromTemplate(file.name);
            }
          }
        },
        (error) => {
          this.uploading = false;
          this.fileError = true;
          this.errorMessage = error?.error?.errorMessage || 'Upload failed.';
        }
      );
  }

  /**
   * Legacy AngularJS `$http` used query params where spaces in `name` appear as `+`
   * (e.g. `name=Efile+Overrides+...`). Angular `HttpParams` encodes spaces as `%20`,
   * which some GTW endpoints treat differently — build the query string explicitly.
   */
  private static buildTemplateNameQueryString(fileName: string): string {
    return encodeURIComponent(fileName).replace(/%20/g, '+');
  }

  private loadGridDataFromTemplate(fileName: string): void {
    const nameQs = ImportUploadDialogComponent.buildTemplateNameQueryString(fileName);
    const url = `${environment.base_url}/getGridDataFromTemplate?name=${nameQs}`;
    this.http.get<any>(url, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response) => {
        if (!response?.data || response.data.length === 0) {
          this.gtwSnackbarService.openSnackBar(
            'Empty file cannot be uploaded. Please make sure file has data.',
            'gtw-snackbar--yellow'
          );
          this.dialogRef.close({ success: false });
          return;
        }
        this.dialogRef.close({
          success: true,
          data: response,
          fileName,
        });
      },
      (error) => {
        this.fileError = true;
        this.errorMessage = error?.error?.errorMessage || 'Failed to process template.';
      }
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
